import { css } from '@emotion/react';
import { startCase } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PhonesUtils } from '@frontend/phone-panels';
import { theme } from '@frontend/theme';
import { Button, Chip, Heading, Text, Truncated } from '@frontend/design-system';
import { Dot } from './dot.component';

type HeaderProps = {
  onBack: () => void;
  callFrom?: string;
  callStartTime?: string;
  callStatus?: string;
};

export const CallPathMappingHeader = ({ onBack, callFrom, callStartTime, callStatus }: HeaderProps) => {
  const { t } = useTranslation('calls');

  const handleFeedback = () => {
    window.open(
      'https://feedback.getweave.com/forums/964997-new-weave-app/suggestions/50542602-improve-call-path-map-details',
      '_blank'
    );
  };

  return (
    <div
      style={{
        display: 'grid',
        alignItems: 'center',
        columnGap: theme.spacing(1),
        gridTemplateColumns: 'auto 1fr',
        gridTemplateAreas: '"back title" ". label"',
      }}
    >
      <Button iconName='caret-left' variant='secondary' css={{ gridArea: 'back' }} onClick={onBack} />
      <Heading
        level={2}
        css={{ margin: 0, gridArea: 'title', display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}
      >
        {t('Call Path')}
        <Button
          onClick={handleFeedback}
          css={{ marginLeft: 'auto' }}
          iconName='user-voice'
          size='large'
          variant='secondary'
          hoverLabel={t('Give feedback')}
          hoverLabelPlacement='left'
        />
      </Heading>
      <Text css={{ gridArea: 'label' }} as='div' size='medium'>
        <div css={styles}>
          <div>
            <Icon name='phone-incoming-small' css={{ color: theme.colors.neutral70 }} />
          </div>
          <Truncated as={Text} size='medium'>
            {t('Call from {{ name }}', { name: callFrom })}
          </Truncated>
          <Dot size='small' />
          <Truncated as={Text} size='medium'>
            {callStartTime}
          </Truncated>
          <Dot size='small' />
          {callStatus && (
            <Chip
              css={{ gridRow: 2, gridColumn: 2, width: 'max-content' }}
              isResponsive
              variant={PhonesUtils.getStatusChipColor(callStatus ?? '')}
            >
              {startCase(callStatus)}
            </Chip>
          )}
        </div>
      </Text>
    </div>
  );
};

const styles = css`
  grid-template-columns: max-content 1fr max-content max-content max-content;
  display: grid;
  gap: ${theme.spacing(1)};
  align-items: center;
`;
